/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Table component for SettingsAutoReports
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.3.0
 */

import { useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../../../components/common';
import {
    loadTableListAction,
    loadTableMeta,
    sendTableFieldsSize,
    setTableSortAction,
} from '../../../../../../components/Table/actions/tableActions';
import { ITableField, ITableReducer } from '../../../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableIsLoading,
    makeSelectorTableSort,
} from '../../../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil from '../../../../../../components/Table/TableColumnUtil';
import {
    IFieldResize,
    ISendTableFieldsSizePayload,
    ISetTableSortPayload,
    TableNameType,
} from '../../../../../../components/Table/types/tableTypes';
import {
    makeSelectPageSettingsAutoReportsTranslation,
    makeSelectTranslationCommon,
} from '../../../../../../modules/internationalization/intSelectors';
import {
    ICommonPageTranslation,
    ICommonTranslation,
    ICorporateTranslation,
} from '../../../../../../modules/internationalization/types';
import {
    IPageSettingsAutoReportsTableTranslation,
    IPageSettingsAutoReportsTitleTranslation,
} from '../../../../../../modules/internationalization/types/pages/pageSettings';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import {
    makeSelectGuardComponents,
    makeSelectIsColumnResizeEnabled,
} from '../../../../../../modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import {
    getSettingsAutoReportsColumnFilterType,
    getSettingsAutoReportsGetColumnCellType,
    SettingsAutoReportsCellConfig,
} from './SettingsAutoReportsTableCells';
import { ESettingsAutoReportsField, SettingsAutoReportsColumnNames } from './settingsAutoReportsTableTypes';

interface ISettingsAutoReportsProps {
    tableCount: number;
}

interface IConnectedProps {
    componentsFeature: guardComponentType;
    data: ITableReducer[TableNameType]['data'];
    fields: ITableReducer[TableNameType]['fields'];
    isDensity: boolean;
    isLoading: boolean;
    isTableResizeEnabled: boolean;
    sort: ITableReducer[TableNameType]['sort'];
    translationCommon: ICommonTranslation & ICorporateTranslation;
    translationTable:
        ICommonPageTranslation<IPageSettingsAutoReportsTableTranslation, IPageSettingsAutoReportsTitleTranslation>;
}

interface IDispatchProps {
    loadTableMeta: () => void;
    loadTableList: () => void;
    sendTableFieldsSize: (payload: ISendTableFieldsSizePayload) => void;
    setTableSort: (payload: ISetTableSortPayload) => void;
}

type TSettingsAutoReportsProps = ISettingsAutoReportsProps & IConnectedProps & IDispatchProps;

const SettingsAutoReportsTable = (props: TSettingsAutoReportsProps) => {
    const tableColumnUtil = new TableColumnUtil<ESettingsAutoReportsField,
        ICommonPageTranslation<IPageSettingsAutoReportsTableTranslation,
            IPageSettingsAutoReportsTitleTranslation> & ICorporateTranslation & ICommonTranslation>(
        {
            cellConfig: SettingsAutoReportsCellConfig,
            columnNames: SettingsAutoReportsColumnNames,
            componentsFeature: props.componentsFeature,
            fields: props.fields as Array<ITableField<ESettingsAutoReportsField>>,
            getColumnCellType: getSettingsAutoReportsGetColumnCellType,
            getColumnFilterType: getSettingsAutoReportsColumnFilterType,
            getColumnFixedType: () => false,
            isTableResizeEnabled: props.isTableResizeEnabled,
            translation: {
                ...props.translationCommon,
                ...props.translationTable,
            },
        });

    useEffect(() => {
        props.loadTableMeta();
    }, []);

    const saveResizedChange = (fields: IFieldResize[]) => {
        props.sendTableFieldsSize({
            fields: fields.map(
                (field) => ({ name: field.id, size: field.value })),
            tableName: TableNameType.settingsAutoReports,
        });
    };

    return (
        <Table
            basketAllIn={false}
            columns={tableColumnUtil.customizeTableColumns()}
            counter={props.tableCount}
            data={props.data}
            fields={props.fields}
            isDensity={props.isDensity}
            isLoading={props.isLoading}
            loadTableList={props.loadTableList}
            saveResizedChange={saveResizedChange}
            setTableSort={props.setTableSort}
            sideBarWidth={264}
            sort={props.sort}
            tableDataName={TableNameType.settingsAutoReports}
        />
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.settingsAutoReports),
    fields: makeSelectorTableFields(TableNameType.settingsAutoReports),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(TableNameType.settingsAutoReports),
    isTableResizeEnabled: makeSelectIsColumnResizeEnabled(),
    sort: makeSelectorTableSort(TableNameType.settingsAutoReports),
    translationCommon: makeSelectTranslationCommon(),
    translationTable: makeSelectPageSettingsAutoReportsTranslation(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadTableMeta: () => dispatch(loadTableMeta({ tableName: TableNameType.settingsAutoReports })),
    loadTableList: () => dispatch(loadTableListAction()),
    sendTableFieldsSize: (payload: ISendTableFieldsSizePayload) => dispatch(sendTableFieldsSize(payload)),
    setTableSort: (payload) => dispatch(setTableSortAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsAutoReportsTable);
